import { call, put, takeLatest } from "redux-saga/effects";
import { replace, routerActions } from "connected-react-router";

import { NO_TRANSITION } from "constants.js";
import * as loans from "middleware/loans";
import { actions as notificationActions } from "reducers/notification";
import { types } from "reducers/loans";
import { downloadPdf, downloadXls, downloadCSVFile } from "util/download";
import * as i18n from "util/i18n";
import { formatNum } from "util/format";
import { delay } from "redux-saga";
import moment from "moment";

const sagas = [
    takeLatest(types.LIST_LOANS_REQUEST, listLoansRequest),
    takeLatest(types.LIST_LOANSV2_REQUEST, listLoansRequestV2),
    takeLatest(types.DETALLE_PRESTAMO_REQUEST, getLoanDetail),
    takeLatest(types.CANCELACION_ANTICIPADA_REQUEST, getDataCancelacionAnticipada),
    takeLatest(types.CANCELACION_ANTICIPADA_PREVIEW_REQUEST, cancelacionAnticipadaPreview),
    takeLatest(types.CANCELACION_ANTICIPADA_PAYOFF_REQUEST, cancelacionAnticipada),
    takeLatest(types.OTROS_PRODUCTOS_REQUEST, otrosProductos),

    takeLatest(types.LOAN_DETAILS_REQUEST, loanDetails),
    takeLatest(types.LOAN_FETCH_MORE_STATEMENTS_REQUEST, fetchMoreStatements),
    takeLatest(types.LOAN_READ_REQUEST, loanRead),
    takeLatest(types.DOWNLOAD_PAYMENT_REQUEST, downloadPayment),

    takeLatest(types.UNSECURED_LOANS_APPLICATION_PRE_REQUEST, unsecuredLoansApplicationPre),
    takeLatest(types.UNSECURED_LOANS_APPLICATION_PREVIEW_REQUEST, unsecuredLoansApplicationPreview),
    takeLatest(types.UNSECURED_LOAN_APPLICATION_SEND_REQUEST, unsecuredLoansApplicationSend),
    takeLatest(types.DOWNLOAD_UNSECURED_LOANS_APPLICATION_REQUEST, downloadUnsecuredLoansApplication),
    takeLatest(types.DOWNLOAD_UNSECURED_LOANS_TERMS_REQUEST, downloadUnsecuredLoansApplicationTerms),
    takeLatest(types.DOWNLOAD_UNSECURED_LOAN_DETAIL_REQUEST, downloadUnsecuredLoanDetail),

    takeLatest(types.DOWNLOAD_UNSECURED_LOAN_FEE_DETAIL_REQUEST, downloadUnsecuredLoanFeeDetail),
    takeLatest(types.CUSTOM_LOAN_REQUEST, sendRequestCustomLoan),
    takeLatest(types.GET_RATES_REQUEST, getRates),
    takeLatest(types.DOWNLOAD_LOANS_DETAIL_REQUEST, downloadLoansDetailRequest),
];

export default sagas;

const defaultFilters = {
    status: "allFees",
};

function* unsecuredLoansApplicationPre({ loanType }) {
    const response = yield call(loans.unsecuredLoansApplicationPre, loanType);
    if (response && response.status === 200) {
        const {
            subproducts,
            qualificationAvailable,
            qualificationLimit,
            outListPeriodicity,
            outListDestinyFund,
            minAmount,
            maxAmount,
        } = response.data.data;

        const periodicityList = outListPeriodicity?.collection?.map((c) => ({
            value: c.code,
            label: c.message,
        }));

        const destinyFundList =
            outListDestinyFund?.length > 0
                ? outListDestinyFund?.map((elem) => ({
                      value: elem.fundDestinyCode,
                      label: elem.fundDestinyDescription,
                  }))
                : null;

        const currency = {
            value: "01",
            label: i18n.get("currency.name.ARS"),
        };

        yield put({
            type: types.UNSECURED_LOANS_APPLICATION_PRE_SUCCESS,
            subproducts,
            qualificationAvailable,
            qualificationLimit,
            periodicityList,
            destinyFundList,
            currency,
            minAmount,
            maxAmount: maxAmount || qualificationAvailable || 999999.0,
        });
    } else {
        yield put({
            type: types.UNSECURED_LOANS_APPLICATION_PRE_FAILURE,
        });
    }
}

function* unsecuredLoansApplicationPreview({ params }) {
    const response = yield call(loans.unsecuredLoansApplicationPreview, params);
    if (response && response.status === 200) {
        const { responseLoanSimulation } = response.data.data;
        const charges = {
            stampTax: responseLoanSimulation ? responseLoanSimulation.cuotas[0]?.impuestoSello : "0.0",
            ivaPerception: responseLoanSimulation ? responseLoanSimulation.cuotas[0]?.percepcionIva : "0.0",
            iibbPerception: responseLoanSimulation ? responseLoanSimulation.cuotas[0]?.ingresosBrutos?.importe : "0.0",
            iva: responseLoanSimulation ? responseLoanSimulation.cuotas[0]?.iva : "0.0",
            jurisdiction: responseLoanSimulation ? responseLoanSimulation.cuotas[0]?.jurisdiccion : "",
        };

        const installmentList = responseLoanSimulation?.cuotas?.map((value) => ({
            number: value.nroCuota,
            totalFee: value.cuotaTotal,
            capital: value.capital,
            iva: value.iva,
            ivaPerception: value.percepcionIva,
            paymentDueDate: moment(value.vencimiento).utc().format("DD/MM/YYYY"),
            liFees: value.cuotaPura,
            installmentInterestDue: value.intereses,
            iibbAmount: value?.ingresosBrutos?.importe ? value?.ingresosBrutos?.importe : 0,
            stampTax: value?.impuestoSello ? value.impuestoSello : 0,
            jurisdictionStamp: responseLoanSimulation ? responseLoanSimulation.impuestoSelloJuris : "N/A",
            jurisdictioniibb: value?.ingresosBrutos?.jurisdiccion ? value?.ingresosBrutos?.jurisdiccion : "N/A",
        }));

        const loanSimulation = {
            totalOperationAmount: responseLoanSimulation?.importe,
            totalAmount: responseLoanSimulation?.importeNeto,
            firstInstallment: responseLoanSimulation?.cuotas[0]?.cuotaTotal,
            loanType: params.loanType,
            destinyFund: {
                label: i18n.get(`unsecured.loans.fundDestiny.${responseLoanSimulation?.destinoFondos}`),
                value: responseLoanSimulation?.destinoFondos,
            },
            interestVariableType: {
                label: "Fija", // POR EL MOMENTO QUEDA SIEMPRE FIJA A LA ESPERA DE BIBANK
                value: "Fija",
            },
            currency: {
                label: i18n.get(`currency.label.${responseLoanSimulation?.codigoMoneda}`),
                value: responseLoanSimulation?.codigoMoneda,
            },
            termType: {
                label: i18n.get(`unsecured.loans.periodicity.${responseLoanSimulation?.periodicidad}`),
                value: responseLoanSimulation?.periodicidad,
            },
            tna: `${responseLoanSimulation?.tasas?.tna}%`,
            tem: `${responseLoanSimulation?.tasas?.tem}%`,
            tea: `${responseLoanSimulation?.tasas?.tea}%`,
            cftSIVA: `${formatNum(responseLoanSimulation?.cft?.cftSinIva * 100)}%`,
            cftCIVA: `${formatNum(responseLoanSimulation?.cft?.cftConIva * 100)}%`,
            installmentList,
            charges,
            operationId: responseLoanSimulation?.idPrestamo,
            productId: responseLoanSimulation?.idPrestamo,
            subproductId: responseLoanSimulation?.tipoPrestamo,

            quotaQuantity: responseLoanSimulation?.cantidadCuotas,
            netAmountReceivable: responseLoanSimulation?.importeNeto,

            firstPureInstallment: responseLoanSimulation?.cuotas[0]?.cuotaTotal || 0,
            lastPureInstallment: installmentList
                ? installmentList[responseLoanSimulation.cuotas.length - 1]?.totalFee || 0
                : 0,
            grantingExpenses: 0,
            commission: responseLoanSimulation?.retenciones?.filter((ret) => ret.codigo === "92")[0]?.monto,
            rateType: i18n.get(`unsecured.loans.simulation.rateType.${responseLoanSimulation?.tipoTasa}`),
            accountLabel: responseLoanSimulation.accountLabel,
            duration: `${responseLoanSimulation.duracion} meses`,
            paymentDate: responseLoanSimulation.paymentDate,
            debitAccount: responseLoanSimulation.debitAccount,
        };
        yield put({
            type: types.UNSECURED_LOANS_APPLICATION_PREVIEW_SUCCESS,
            loanSimulation,
        });
    } else {
        yield put({
            type: types.UNSECURED_LOANS_APPLICATION_PREVIEW_FAILURE,
        });
    }
}

function* unsecuredLoansApplicationSend({ params }) {
    const response = yield call(loans.unsecuredLoansApplicationSend, params);
    if (response && response.status === 200) {
        yield put({
            type: types.UNSECURED_LOAN_APPLICATION_SEND_SUCCESS,
        });
        const { code } = response.data;
        if (response && code !== "COR020W") {
            yield put(replace(`/requestUnsecuredLoan/transaction/${response.data.idTransaction}`));
        }
    } else {
        yield put({
            type: types.UNSECURED_LOAN_APPLICATION_SEND_FAILURE,
        });
    }
}

function* downloadUnsecuredLoansApplicationTerms() {
    const { type, data } = yield call(loans.downloadUnsecuredLoanApplicationTerms);

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_UNSECURED_LOANS_TERMS_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["loans"]));
    } else {
        const { content, fileName } = data.data;

        downloadPdf(fileName, content);

        yield put({ type: types.DOWNLOAD_UNSECURED_LOANS_TERMS_SUCCESS });
    }
}

function* downloadUnsecuredLoansApplication({ unsecuredLoanPreview, accountLabel, format }) {
    const { type, data } = yield call(
        loans.downloadUnsecuredLoanApplication,
        unsecuredLoanPreview,
        accountLabel,
        format,
    );

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_UNSECURED_LOANS_APPLICATION_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["loans"]));
    } else {
        const { content, fileName } = data.data;

        if (format === "pdf") {
            downloadPdf(fileName, content);
        }

        yield put({ type: types.DOWNLOAD_UNSECURED_LOANS_APPLICATION_SUCCESS });
    }
}

function* downloadUnsecuredLoanDetail({ loanDescription, product, installmentsStatus, format }) {
    const { type, data } = yield call(
        loans.downloadUnsecuredLoanDetail,
        loanDescription,
        product,
        installmentsStatus,
        format,
    );

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_UNSECURED_LOAN_DETAIL_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["loans"]));
    } else {
        const { content, fileName } = data.data;

        if (format === "pdf") {
            downloadPdf(fileName, content);
        }

        yield put({ type: types.DOWNLOAD_UNSECURED_LOAN_DETAIL_SUCCESS });
    }
}

function* downloadUnsecuredLoanFeeDetail({ unsecuredLoanFeeDetail, format }) {
    const { type, data } = yield call(loans.downloadUnsecuredLoanFeeDetail, unsecuredLoanFeeDetail, format);

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_UNSECURED_LOAN_FEE_DETAIL_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["loans"]));
    } else {
        const { content, fileName } = data.data;

        if (format === "pdf") {
            downloadPdf(fileName, content);
        }

        yield put({ type: types.DOWNLOAD_UNSECURED_LOAN_FEE_DETAIL_SUCCESS });
    }
}

function* getDataCancelacionAnticipada({ prestamo }) {
    yield put(replace(`/loans/cancelacionAnticipada/${prestamo}`));
}
function* getLoanDetail({ loanNumber }) {
    const response = yield call(loans.loanDetail, loanNumber);
    if (response && response.status === 200) {
        const detallePrestamo = response.data.data.loan ?? {};
        yield put({
            type: types.DETALLE_PRESTAMO_SUCCESS,
            detallePrestamo,
        });
    } else {
        yield put(routerActions.push("/loans"));
        yield put(notificationActions.showNotification(response.data.message, "error", ["loans"]));
    }
}

function* listLoansRequestV2() {
    try {
        const response = yield call(loans.listLoansV2);

        if (response && response.status === 200) {
            const activeLoans = response.data.data.activeLoans ?? [];
            const historicalLoans = response.data.data.historicalLoans ?? [];

            yield put({
                type: types.LIST_LOANSV2_SUCCESS,
                activeLoans,
                historicalLoans,
            });
        }
    } finally {
        yield put({
            type: types.UNSECURED_LOAN_SET_LOADING,
            loading: false,
        });
    }
}
function* cancelacionAnticipada({ idProduct, extraInfo, _otp, debitAccount, detallePrestamo, idTransaction }) {
    const params = idTransaction
        ? {
              _otp,
              idTransaction,
          }
        : {
              product: { idProduct, extraInfo },
              loan: { ...detallePrestamo },
              _otp,
              debitAccount,
          };

    const response = yield call(loans.cancelacionAnticipada, params);
    const { code } = response.data;
    if (response && code !== "COR020W") {
        yield put({
            type: types.DCANCELACION_ANTICIPADA_PAYOFF_SUCCESS,
        });
        yield put(replace(`/loans/cancelacionAnticipadaTicket/${response.data.idTransaction}`));
    } else {
        yield put({
            type: types.DCANCELACION_ANTICIPADA_PAYOFF_FAILURE,
        });
    }
}
function* cancelacionAnticipadaPreview({ idProduct, extraInfo, id, idTransaction }) {
    try {
        const response = yield call(loans.cancelacionAnticipadaPreview, { product: { idProduct, extraInfo } });
        const { code } = response.data;
        if (response && code === "COR000I") {
            const { loan } = response.data.data;
            yield put({
                type: types.CANCELACION_ANTICIPADA_PREVIEW_SUCCESS,
                cancelacionPreview: loan,
            });
            yield put(replace(`/loans/cancelacionAnticipada/${id}`, { idTransaction }));
        } else {
            yield put({
                type: types.CANCELACION_ANTICIPADA_PREVIEW_FAILURE,
            });
        }
    } finally {
        yield put({
            type: types.UNSECURED_LOAN_APPLICATION_SET_BUTTON_LOADING,
            cancelacionPreview: false,
        });
    }
}
function* otrosProductos({ email, credential, phone, comment, typeProduct, dataAll }) {
    const ticketStates = {
        COR020W: "INVALID_CREDENTIAL",
        COR023I: "PENDING",
        COR000I: "FINISHED",
        API560W: "OMA_API_INTERNAL_ERROR",
    };
    const params = {
        user: dataAll,
        mobileNumber: phone,
        corporateMail: email,
        productType: typeProduct,
        comment,
        _password: credential,
    };
    const response = yield call(loans.otrosProductos, params);
    const { code } = response.data;
    if (response && code !== "COR020W") {
        yield put({
            type: types.OTROS_PRODUCTOS_SUCCESS,
        });
        yield put(replace(`/loans/otrosProductosSucccessTicket/${response.data.idTransaction}`));
        return;
    }
    yield put({
        type: types.OTROS_PRODUCTOS_FAILURE,
        statusLoan: ticketStates[code],
    });
}

function* listLoansRequest() {
    const response = yield call(loans.listLoans);
    if (response && response.status === 200) {
        let loansList = response.data.data.amortizedLoans;
        const { requestLoanPermission } = response.data.data;
        const { amortizedLoans, propertiesLoans } = response.data.data;
        loansList = amortizedLoans !== null ? amortizedLoans.concat(propertiesLoans) : propertiesLoans;
        if (loansList.length === 1) {
            yield put(
                replace({
                    pathname: `/loans/${loansList[0].idProduct}`,
                    state: { transition: NO_TRANSITION },
                }),
            );
        } else {
            yield put({
                type: types.LIST_LOANS_SUCCESS,
                loansList,
                requestLoanPermission,
            });
        }
    }
}

function* loanDetails({ selectedLoanId, filters = defaultFilters }) {
    const response = yield call(loans.listStatements, selectedLoanId, filters);
    if (response && response.status === 200) {
        yield put({
            type: types.LOAN_DETAILS_SUCCESS,
            ...response.data.data,
        });
    }
}

function* fetchMoreStatements({ loanId, filters = defaultFilters }) {
    const response = yield call(loans.listStatements, loanId, filters);

    if (response && response.status === 200) {
        yield put({
            type: types.LOAN_FETCH_MORE_STATEMENTS_SUCCESS,
            ...response.data.data,
        });
    }
}

function* loanRead({ loanId }) {
    const response = yield call(loans.readLoan, loanId);

    if (response && response.status === 200) {
        yield put({
            type: types.LOAN_READ_SUCCESS,
            ...response.data.data,
        });
    }
}

function* downloadPayment({ idLoan, format }) {
    const { type, data } = yield call(loans.downloadPayment, idLoan, defaultFilters, format);

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_PAYMENT_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["loan/details"]));
    } else {
        const { content, fileName } = data.data;

        if (format === "pdf") {
            downloadPdf(fileName, content);
        } else if (format === "xls") {
            downloadXls(fileName, content);
        } else if (format === "csv") {
            downloadCSVFile(fileName, content);
        }

        yield put({ type: types.DOWNLOAD_PAYMENT_SUCCESS });
    }
}

function* sendRequestCustomLoan({ params, formikBag, credentialType }) {
    const response = yield call(loans.requestCustomLoan, params);

    if (response && response.status === 200) {
        const { code } = response.data;
        if (response && code === "API515I") {
            yield put(routerActions.push("/loans"));
            yield call(delay, 500);

            yield put(
                notificationActions.showNotification(i18n.get("request.custom.loan.success.message"), "success", [
                    "loans",
                ]),
            );
            yield put({
                type: types.CUSTOM_LOAN_SUCCESS,
            });
        } else if (response.type === "W") {
            yield put({ type: types.CUSTOM_LOAN_FAILURE });
            if (response.data.code === "COR020W" && response?.data?.data?.otp) {
                const msj =
                    credentialType === "password"
                        ? "preferences.password.password.incorrect"
                        : "login.step1.secondFactor.invalid";

                yield put(notificationActions.showNotification(i18n.get(msj), "error", ["loans"]));
                formikBag.setErrors({
                    credential: i18n.get(msj),
                });
            } else {
                yield put(notificationActions.showNotification(response.data.message, "error", ["loans"]));
            }
        }
    } else {
        yield put({
            type: types.CUSTOM_LOAN_FAILURE,
        });
    }

    formikBag.setSubmitting(false);
}

function* getRates({ currency }) {
    const params = {
        currency,
    };
    const response = yield call(loans.getRates, params);
    const { code, data } = response.data;
    if (response && code !== "COR020W") {
        yield put({
            type: types.GET_RATES_SUCCESS,
            rates: data.rates,
            preferentialRates: data.preferentialRates,
        });
        if (data.errorMessage) {
            yield put(notificationActions.showNotification(data.errorMessage, "error", ["loans"]));
        }
    }
    yield put({
        type: types.GET_RATES_FAILURE,
    });
}

function* downloadLoansDetailRequest({ installmentList, format }) {
    const { type, data } = yield call(loans.downloadLoansDetailRequest, installmentList, format);

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_LOANS_DETAIL_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["loans"]));
    } else {
        const { content, fileName } = data.data;
        downloadPdf(fileName, content);
        yield put({ type: types.DOWNLOAD_LOANS_DETAIL_SUCCESS });
    }
}
